import React from 'react';
import { ArrowDefs } from './dg';

/* ------------------------------------------------------------------ */
/* 1. The cadence gap: a monthly survey vs. a shelf that reprices     */
/* ------------------------------------------------------------------ */

export function DiagramGap() {
    return (
        <svg
            viewBox="0 0 720 262"
            role="img"
            aria-label="Left: official statistics publish one revised number a month. Right: a supermarket shelf reprices every week."
        >
            <rect x="8" y="8" width="344" height="230" rx="14" className="d-panel" />
            <rect x="368" y="8" width="344" height="230" rx="14" className="d-panel" />
            <text x="180" y="40" textAnchor="middle" className="d-panel-title">
                what gets published
            </text>
            <text x="540" y="40" textAnchor="middle" className="d-panel-title">
                what the shelf does
            </text>

            {/* monthly step */}
            <line x1="48" y1="188" x2="312" y2="188" className="d-axis" />
            <line x1="48" y1="60" x2="48" y2="188" className="d-axis" />
            <path
                d="M48 150 H110 V142 H172 V136 H234 V130 H312"
                className="d-edge-strong"
                fill="none"
            />
            {[
                [48, 150],
                [110, 142],
                [172, 136],
                [234, 130],
                [312, 130],
            ].map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="4.5" className="d-dot" />
            ))}
            <text x="180" y="210" textAnchor="middle" className="d-sub">
                one number, revised monthly
            </text>

            {/* weekly path */}
            <line x1="404" y1="188" x2="676" y2="188" className="d-axis" />
            <line x1="404" y1="60" x2="404" y2="188" className="d-axis" />
            <path
                d="M404 148 L440 126 L476 138 L512 104 L548 118 L584 92 L620 110 L676 96"
                className="d-edge-strong"
                fill="none"
            />
            {[
                [404, 148],
                [440, 126],
                [476, 138],
                [512, 104],
                [548, 118],
                [584, 92],
                [620, 110],
                [676, 96],
            ].map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="4.5" className="d-dot" />
            ))}
            <text x="540" y="210" textAnchor="middle" className="d-sub">
                repriced every week, sometimes twice
            </text>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 2. The fleet: four storefronts, one collector each, one contract   */
/* ------------------------------------------------------------------ */

export function DiagramFleet() {
    const stores = [
        ['Nature’s Basket', '55 items', 30],
        ['Spencer’s', '47 items', 86],
        ['Modern Bazaar', '145 items', 142],
        ['DMart Ready', '34 items', 198],
    ];
    return (
        <svg
            viewBox="0 0 720 300"
            role="img"
            aria-label="Four storefronts feed one Scraper Studio collector each; the watchdog validates every run before the index engine sees it."
        >
            <ArrowDefs id="m2a" />
            {stores.map(([name, sub, y]) => (
                <g key={name}>
                    <rect x="16" y={y} width="152" height="44" rx="9" className="d-box" />
                    <text x="92" y={y + 19} textAnchor="middle" className="d-title">
                        {name}
                    </text>
                    <text x="92" y={y + 34} textAnchor="middle" className="d-sub">
                        {sub}
                    </text>
                    <line
                        x1="168"
                        y1={y + 22}
                        x2="184"
                        y2={y + 22}
                        className="d-edge-thin"
                    />
                </g>
            ))}
            <line x1="184" y1="52" x2="184" y2="220" className="d-edge-thin" />
            <line
                x1="184"
                y1="140"
                x2="204"
                y2="140"
                className="d-edge"
                markerEnd="url(#m2a)"
            />

            <rect x="206" y="74" width="160" height="132" rx="12" className="d-box accent" />
            <text x="286" y="112" textAnchor="middle" className="d-title">
                Scraper Studio
            </text>
            <text x="286" y="132" textAnchor="middle" className="d-sub">
                one collector per chain
            </text>
            <text x="286" y="148" textAnchor="middle" className="d-sub">
                c_* IDs never change
            </text>
            <text x="286" y="172" textAnchor="middle" className="d-sub">
                proxies · retries · unblocking
            </text>

            <line
                x1="366"
                y1="140"
                x2="396"
                y2="140"
                className="d-edge"
                markerEnd="url(#m2a)"
            />
            <rect x="398" y="74" width="140" height="132" rx="12" className="d-box" />
            <text x="468" y="112" textAnchor="middle" className="d-title">
                watchdog
            </text>
            <text x="468" y="132" textAnchor="middle" className="d-sub">
                four drift checks
            </text>
            <text x="468" y="148" textAnchor="middle" className="d-sub">
                pass, or heal
            </text>
            <text x="468" y="172" textAnchor="middle" className="d-sub">
                every verdict logged
            </text>

            <line
                x1="538"
                y1="140"
                x2="568"
                y2="140"
                className="d-edge"
                markerEnd="url(#m2a)"
            />
            <rect x="570" y="74" width="134" height="132" rx="12" className="d-box" />
            <text x="637" y="112" textAnchor="middle" className="d-title">
                index engine
            </text>
            <text x="637" y="132" textAnchor="middle" className="d-sub">
                ₹/kg · ₹/L
            </text>
            <text x="637" y="148" textAnchor="middle" className="d-sub">
                median, then chain
            </text>
            <text x="637" y="172" textAnchor="middle" className="d-sub">
                base 100
            </text>

            <text x="360" y="252" textAnchor="middle" className="d-sub">
                four very different sites, one output contract: title · price · pack_size · brand · url
            </text>
            <text x="360" y="274" textAnchor="middle" className="d-sub">
                a repaired collector keeps its ID, so the pipeline downstream never notices
            </text>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 3. The index math, left to right                                   */
/* ------------------------------------------------------------------ */

export function DiagramMath() {
    const nodes = [
        ['₹49 / 500 g', 'pack price', 16],
        ['₹98 / kg', 'unit price', 160],
        ['₹93 / kg', 'median listing', 304],
        ['0.947', 'today ÷ day one', 448],
        ['100 → 103.1', 'chained index', 592],
    ];
    return (
        <svg
            viewBox="0 0 720 210"
            role="img"
            aria-label="A pack price is normalized to a unit price, reduced to a median across listings, divided by its day-one price, and chained into a base-100 index."
        >
            <ArrowDefs id="m3a" />
            {nodes.map(([title, sub, x], i) => (
                <g key={title}>
                    <rect x={x} y="30" width="120" height="76" rx="10" className={i === 4 ? 'd-box accent' : 'd-box'} />
                    <text x={x + 60} y={i === 4 ? 62 : 58} textAnchor="middle" className="d-title">
                        {title}
                    </text>
                    <text x={x + 60} y={i === 4 ? 84 : 80} textAnchor="middle" className="d-sub">
                        {sub}
                    </text>
                    {i < nodes.length - 1 && (
                        <line
                            x1={x + 122}
                            y1="68"
                            x2={x + 158}
                            y2="68"
                            className="d-edge"
                            markerEnd="url(#m3a)"
                        />
                    )}
                </g>
            ))}
            <text x="360" y="140" textAnchor="middle" className="d-sub">
                Amul 500 ml at ₹33 and Amul 1 L at ₹66 are the same price. Unit prices make that true.
            </text>
            <text x="360" y="162" textAnchor="middle" className="d-sub">
                A mis-scraped ₹9,900 “milk” is one listing out of dozens. The median ignores it.
            </text>
            <text x="360" y="184" textAnchor="middle" className="d-sub">
                Base 100 means the first scan is the reference: an index of 103.1 is the same basket, 3.1% dearer.
            </text>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 4. The heal loop                                                   */
/* ------------------------------------------------------------------ */

export function DiagramLoop() {
    return (
        <svg
            viewBox="0 0 720 276"
            role="img"
            aria-label="A weekly run is collected and validated. A critical drift opens an incident, dispatches a heal, and the repaired collector is re-run and verified before storage."
        >
            <ArrowDefs id="m4a" />
            <rect x="24" y="26" width="130" height="64" rx="10" className="d-box" />
            <text x="89" y="53" textAnchor="middle" className="d-title">
                weekly cron
            </text>
            <text x="89" y="73" textAnchor="middle" className="d-sub">
                triggers the fleet
            </text>

            <rect x="178" y="26" width="140" height="64" rx="10" className="d-box" />
            <text x="248" y="53" textAnchor="middle" className="d-title">
                collect
            </text>
            <text x="248" y="73" textAnchor="middle" className="d-sub">
                rows[] come back
            </text>

            <rect x="342" y="26" width="150" height="64" rx="10" className="d-box" />
            <text x="417" y="53" textAnchor="middle" className="d-title">
                validate
            </text>
            <text x="417" y="73" textAnchor="middle" className="d-sub">
                four checks, one verdict
            </text>

            <rect x="516" y="26" width="180" height="64" rx="10" className="d-box d-ok" />
            <text x="606" y="53" textAnchor="middle" className="d-title">
                store
            </text>
            <text x="606" y="73" textAnchor="middle" className="d-sub">
                append-only observations
            </text>

            <line x1="154" y1="58" x2="176" y2="58" className="d-edge" markerEnd="url(#m4a)" />
            <line x1="318" y1="58" x2="340" y2="58" className="d-edge" markerEnd="url(#m4a)" />
            <line x1="492" y1="58" x2="514" y2="58" className="d-edge" markerEnd="url(#m4a)" />

            <rect x="342" y="150" width="150" height="64" rx="10" className="d-box d-danger" />
            <text x="417" y="177" textAnchor="middle" className="d-title">
                incident
            </text>
            <text x="417" y="197" textAnchor="middle" className="d-sub">
                reason + prompt + receipt
            </text>

            <rect x="150" y="150" width="160" height="64" rx="10" className="d-box accent" />
            <text x="230" y="177" textAnchor="middle" className="d-title">
                heal
            </text>
            <text x="230" y="197" textAnchor="middle" className="d-sub">
                refactor → approve
            </text>

            <line x1="417" y1="90" x2="417" y2="148" className="d-edge-danger" markerEnd="url(#m4a)" />
            <line x1="342" y1="182" x2="312" y2="182" className="d-edge" markerEnd="url(#m4a)" />
            <path
                d="M230 150 V118 H248 V92"
                className="d-edge-ok"
                fill="none"
                markerEnd="url(#m4a)"
            />

            <text x="360" y="242" textAnchor="middle" className="d-sub">
                the first heal of a collector is supervised; after one verified success it heals autonomously
            </text>
            <text x="360" y="264" textAnchor="middle" className="d-sub">
                the Collector ID survives the repair, so no URL, schema, or downstream job is rewritten
            </text>
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 5. Three real receipts, same duration                              */
/* ------------------------------------------------------------------ */

const receipts = [
    {
        when: '04:24 UTC',
        store: 'Nature’s Basket',
        kind: 'schema_drift',
        saw: 'every product nested inside one row',
        prompt: '“Flatten output: one product per row with title, price, pack_size, brand, url”',
        x: 8,
    },
    {
        when: '09:24 UTC',
        store: 'DMart Ready',
        kind: 'empty_run',
        saw: '46 product pages, zero listings',
        prompt: '“Rewrite collector to headless-browser render, wait for product grid selectors”',
        x: 248,
    },
    {
        when: '12:24 UTC',
        store: 'Spencer’s',
        kind: 'null_ratio',
        saw: 'pack_size missing on most rows',
        prompt: '“Re-extract pack size from product attributes for unit-price comparison”',
        x: 488,
    },
];

export function DiagramReceipts() {
    return (
        <svg
            viewBox="0 0 720 258"
            role="img"
            aria-label="Three incidents, each detected by the watchdog and resolved fourteen minutes later by a dispatched heal."
        >
            {receipts.map((r) => (
                <g key={r.store}>
                    <rect x={r.x} y="8" width="224" height="238" rx="12" className="d-panel" />
                    <text x={r.x + 18} y="36" className="d-panel-title">
                        {r.when}
                    </text>
                    <text x={r.x + 18} y="62" className="d-title">
                        {r.store}
                    </text>
                    <text x={r.x + 18} y="84" className="d-sub">
                        {r.kind}
                    </text>
                    <text x={r.x + 18} y="104" className="d-sub">
                        {r.saw}
                    </text>
                    <line x1={r.x + 18} y1="120" x2={r.x + 206} y2="120" className="d-edge-thin" />
                    <text x={r.x + 18} y="144" className="d-sub">
                        prompt sent:
                    </text>
                    <foreignObject x={r.x + 18} y="152" width="188" height="52">
                        <div
                            xmlns="http://www.w3.org/1999/xhtml"
                            style={{
                                fontSize: '11px',
                                lineHeight: 1.4,
                                color: 'var(--d-muted)',
                            }}
                        >
                            {r.prompt}
                        </div>
                    </foreignObject>
                    <text x={r.x + 18} y="226" className="d-note-ok">
                        healed + verified · 14 min
                    </text>
                </g>
            ))}
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 6. The basket matrix                                               */
/* ------------------------------------------------------------------ */

const matrixRows = [
    ['Amul butter 500 g', '₹285', '₹298', '₹279', 2, 2],
    ['Tata salt 1 kg', '₹28', '₹27', '₹30', 1, 1],
    ['Aashirvaad atta 5 kg', '₹265', '✕', '₹259', 2, 2],
    ['Tomato 1 kg', '₹27.50', '₹100', '₹64', 0, 0],
];

export function DiagramMatrix() {
    const cols = [300, 430, 560];
    return (
        <svg
            viewBox="0 0 720 252"
            role="img"
            aria-label="A basket matrix: four items priced at three stores, with the cheapest cell in each row highlighted."
        >
            <rect x="8" y="8" width="704" height="236" rx="14" className="d-panel" />
            <text x="28" y="40" className="d-panel-title">
                the basket
            </text>
            {['nature’s basket', 'spencer’s', 'modern bazaar'].map((label, i) => (
                <text key={label} x={cols[i]} y="40" textAnchor="middle" className="d-panel-title">
                    {label}
                </text>
            ))}
            <line x1="24" y1="52" x2="696" y2="52" className="d-edge-thin" />

            {matrixRows.map(([item, a, b, c, cheapCol, row], i) => {
                const y = 78 + i * 40;
                const cells = [a, b, c];
                return (
                    <g key={item}>
                        <text x="28" y={y + 5} className="d-sub" style={{ fontSize: '13px' }}>
                            {item}
                        </text>
                        {cells.map((value, j) => (
                            <g key={j}>
                                {j === cheapCol && value !== '✕' && (
                                    <rect
                                        x={cols[j] - 42}
                                        y={y - 14}
                                        width="84"
                                        height="26"
                                        rx="6"
                                        className="d-callout"
                                    />
                                )}
                                <text
                                    x={cols[j]}
                                    y={y + 5}
                                    textAnchor="middle"
                                    className={j === cheapCol && value !== '✕' ? 'd-callout-text' : 'd-sub'}
                                    style={{ fontSize: '13px' }}
                                >
                                    {value}
                                </text>
                            </g>
                        ))}
                        {i < matrixRows.length - 1 && (
                            <line x1="24" y1={y + 18} x2="696" y2={y + 18} className="d-edge-thin" />
                        )}
                    </g>
                );
            })}
            <text x="360" y="230" textAnchor="middle" className="d-sub">
                the cheapest cell is per item; the total at the bottom is per store
            </text>
        </svg>
    );
}
